import {Pipe, PipeTransform} from "@angular/core";
import {UtilsService} from "framework/services/utils.service";

@Pipe({
    name: "o3TitleCase",
    standalone: false
})
export class TitleCaseFormatPipe implements PipeTransform
{
    transform(value: any): string
    {
        return TitleCaseFormatPipe.format(value);
    }

    public static format(value: any): string
    {
        if (!value) return "";

        return value.toString()
                    .replace(/([a-z])([A-Z])/g, "$1 $2") // insert a space between lower & upper
                    .replace(/\b([A-Z]+)([A-Z])([a-z])/, "$1 $2$3") // space before last upper in a sequence followed by lower
                    .replace(/^./, function (str: string) { return str.toUpperCase(); }); // uppercase the first character
    }
}

@Pipe({
    name: "o3NumberWithSeparators",
    standalone: false
})
export class NumberWithSeparatorsPipe implements PipeTransform
{
    transform(value: number): string
    {
        return NumberWithSeparatorsPipe.format(value);
    }

    public static format(value: number,
                         useGrouping: boolean = true): string
    {
        if (value === undefined || value === null) return "";

        let formatter = new Intl.NumberFormat(undefined, {
            useGrouping: useGrouping
        });

        return formatter.format(value);
    }
}

@Pipe({
    name: "o3Currency",
    standalone: false
})
export class CurrencyPipe implements PipeTransform
{
    transform(value: number,
              currency?: string): string
    {
        return CurrencyPipe.format(value, currency);
    }

    public static format(value: number,
                         currency: string    = "USD",
                         decimal: number     = 2,
                         showApprox: boolean = true): string
    {
        if (value === undefined || value === null) return "";

        const currencyLocale = getLocaleForCurrency(currency);
        const symbol         = getCurrencySymbol(currency);

        /**
         * Formats currency using a disambiguated symbol map to avoid ambiguous symbols like "$".
         * Intl.NumberFormat with 'currency' style often returns unclear symbols (e.g., "$" for both USD and AUD),
         * so we use 'decimal' style and prepend the correct symbol manually.
         */
        const formatter = new Intl.NumberFormat(currencyLocale, {
            style                : "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: decimal
        });

        // Check if the formatted value rounds to zero
        const roundedValue = parseFloat(value.toFixed(decimal));

        if (value < 1 && value > 0 && roundedValue == 0)
        {
            const roundValue = parseFloat(value.toFixed(5));
            return roundValue != 0 ? `${symbol}${roundValue}` : `<${symbol}0.00001`;
        }

        if (roundedValue === 0)
        {
            return `${symbol}0`;
        }

        if (Math.abs(value) < 0.01 && showApprox)
        {
            return value < 0 ? `<${symbol}0` : `<${symbol}0.01`;
        }

        return `${symbol}${formatter.format(value)}`;
    }
}

@Pipe({
    name: "csUnderscoresForPipes",
    standalone: false
})
export class UnderscoresForSpacesPipe implements PipeTransform
{
    transform(value: string): string
    {
        return UnderscoresForSpacesPipe.format(value);
    }

    public static format(value: string): string
    {
        return UtilsService.replaceUnderscoresWithSpaces(value);
    }
}

@Pipe({
    name: "o3Percentage",
    standalone: false
})
export class PercentagePipe implements PipeTransform
{
    transform(value: number): string
    {
        return PercentagePipe.format(value);
    }

    public static format(value: number,
                         fractionDigits?: number): string
    {
        if (value === undefined || value === null) value = 0;

        return `${value.toFixed(fractionDigits || 1)}%`;
    }

    public static computeSafely(numerator: number,
                                denominator: number): number
    {
        if (isNaN(denominator) || denominator == 0) return 0;

        return numerator * 100 / denominator;
    }

    public static computeSafelyClamped(numerator: number,
                                       denominator: number): number
    {
        return UtilsService.clamp(0, 100, PercentagePipe.computeSafely(numerator, denominator));
    }
}

const CurrencyLocaleMap: Record<string, string> = {
    "AED": "ar-AE",
    "ALL": "sq-AL",
    "ARS": "es-AR",
    "AUD": "en-AU",
    "BAM": "bs-BA",
    "BDT": "bn-BD",
    "BGN": "bg-BG",
    "BHD": "ar-BH",
    "BRL": "pt-BR",
    "CAD": "en-CA",
    "CHF": "de-CH",
    "CLP": "es-CL",
    "CNY": "zh-CN",
    "COP": "es-CO",
    "CRC": "es-CR",
    "CZK": "cs-CZ",
    "DKK": "da-DK",
    "EGP": "ar-EG",
    "EUR": "de-DE",
    "GBP": "en-GB",
    "HKD": "zh-HK",
    "HRK": "hr-HR",
    "HUF": "hu-HU",
    "IDR": "id-ID",
    "ILS": "he-IL",
    "INR": "en-IN",
    "ISK": "is-IS",
    "JPY": "ja-JP",
    "KES": "sw-KE",
    "KRW": "ko-KR",
    "KWD": "ar-KW",
    "KZT": "kk-KZ",
    "MOP": "zh-MO",
    "MVR": "dv-MV",
    "MXN": "es-MX",
    "MYR": "ms-MY",
    "NOK": "nb-NO",
    "NZD": "en-NZ",
    "OMR": "ar-OM",
    "PEN": "es-PE",
    "PHP": "en-PH",
    "PKR": "ur-PK",
    "PLN": "pl-PL",
    "QAR": "ar-QA",
    "RON": "ro-RO",
    "RSD": "sr-RS",
    "RUB": "ru-RU",
    "SAR": "ar-SA",
    "SEK": "sv-SE",
    "SGD": "en-SG",
    "THB": "th-TH",
    "TRY": "tr-TR",
    "TWD": "zh-TW",
    "USD": "en-US",
    "VND": "vi-VN",
    "ZAR": "en-ZA"
};

const DisambiguatedSymbolMap: Record<string, string> = {
    AUD: "A$",
    CAD: "CA$",
    NZD: "NZ$",
    SGD: "S$",
    HKD: "HK$",
    USD: "$",
    EUR: "€",
    GBP: "£",
    INR: "₹",
    JPY: "¥",
    KRW: "₩",
    CNY: "¥",
    PHP: "₱",
    RUB: "₽",
    THB: "฿",
    VND: "₫",
    DKK: "Kr",
    SEK: "kr",
    OMR: "﷼",
    BRL: "R$",
    TWD: "NT$",
    AED: "AED",
    IDR: "IDR"
};

export function getLocaleForCurrency(currency: string): string
{
    return CurrencyLocaleMap[currency] || CurrencyLocaleMap["USD"]; // Default to US format if currency is unknown
}

export function getCurrencySymbol(currency: string): string
{
    return DisambiguatedSymbolMap[currency] || currency;
}

@Pipe({
    name: "decodeHtmlEntities",
    standalone: false
})
export class DecodeHtmlEntitiesPipe implements PipeTransform
{
    private static parser = new DOMParser();

    transform(value: string): string
    {
        return DecodeHtmlEntitiesPipe.format(value);
    }

    public static format(value: string): string
    {
        const doc = this.parser.parseFromString(value, "text/html");
        return doc.documentElement.textContent || "";
    }
}
